/**
 * API configuration constants.
 * Centralizes all API URLs and Auth0 configuration for consistency.
 */

/** CloudFront CDN base URL for public/free data */
export const CDN_BASE_URL = process.env.REACT_APP_CDN_BASE_URL || 'https://dfl3la3808yap.cloudfront.net';

/** CloudFront CDN base URL for rendered SEC source documents */
export const SEC_SOURCE_CDN_BASE_URL = process.env.REACT_APP_SEC_SOURCE_CDN_BASE_URL || 'https://d2gyu1782e8i8.cloudfront.net';

/** API Gateway base URL for authenticated requests */
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://mwl26uv0m7.execute-api.us-east-1.amazonaws.com/prod';

/** Auth validation endpoint */
export const AUTH_VALIDATE_URL = `${API_BASE_URL}/validate`;

/** Research Suite authenticated data endpoint */
export const RESEARCH_DATA_URL = `${API_BASE_URL}/research-data`;

/** Favorites API endpoint */
export const FAVORITES_URL = `${API_BASE_URL}/favorites`;

/** Checkout API endpoint */
export const CHECKOUT_URL = `${API_BASE_URL}/checkout`;

/** Stripe customer portal endpoint */
export const PORTAL_URL = `${API_BASE_URL}/create-portal-session`;

/** Welcome email intent endpoint */
export const WELCOME_EMAIL_INTENT_URL = `${API_BASE_URL}/welcome-email-intent`;

/** Contact form API endpoint */
export const CONTACT_URL = `${API_BASE_URL}/contact`;

/** Auth0 API audience for token requests */
export const AUTH0_AUDIENCE = process.env.REACT_APP_AUTH0_AUDIENCE || 'https://api.softwareiq.io';

/** Auth0 scopes for API access */
export const AUTH0_SCOPES = 'openid profile email offline_access';

// ============================================
// File Keys for API Requests
// ============================================

export const FILE_KEYS = {
  COMPSHEET: 'compsheet.json',
  VALUATION: 'valuation.json',
  TRENDED: 'trended.json',
  INDEX: 'index.json',
  COVERAGE: 'coverage.json',
  UNICORN: 'unicorn.json',
  GROWTH_CURVE: 'growthcurve.json',
  JOB_POSTINGS: 'jobPostings.v2.json',
  SI_JOB_POSTINGS: 'siJobPostings.json',
  SI_JOB_MARKET: 'siJobMarket.json',
  DEVMETRICS: 'devmetrics/developerMetrics.json',
  JOB_POSTINGS_DAILY: 'data/charts/jobPostingsDaily.v2.json',
  DEVELOPER_METRICS_DAILY: 'data/devmetrics/developerMetricsDaily.json',
  EXCHANGE_VOLUME_ALL: 'data/exchangeVolume/all.json',
  EXCHANGE_VOLUME_FREE_ALL: 'data/exchangeVolume/free/all.json',
  EXCHANGE_VOLUME_SUMMARY: 'data/exchangeVolume/summary.json',
  TECHSTACK_SUMMARY: 'data/stockpage/techstack/summary.json',
  TECHSTACK_ALL: 'data/stockpage/techstack/all.json',
  TECHSTACK_CATEGORIES: 'data/stockpage/techstack/categories.json',
  TECHSTACK_CATEGORIES_TRENDS: 'data/stockpage/techstack/categories-trends.json',
};

export const SECURITY_ANALYSIS_PUBLIC_SAMPLE_TICKER = 'MDB';
export const SECURITY_ANALYSIS_PRIVATE_SAMPLE_TICKER = 'PVT-DBRK';
export const SECURITY_ANALYSIS_PUBLIC_SAMPLE_DEVELOPER_PROJECT_ID = 'MongoDB:Platform';
export const FREE_SECURITY_ANALYSIS_SAMPLE_CHIP_LABEL = 'Free sample';
export const SECURITY_ANALYSIS_SAMPLE_DATASET_CTA_LABEL = 'Explore sample dataset';

export const FREE_SECURITY_ANALYSIS_TICKERS = [
  SECURITY_ANALYSIS_PUBLIC_SAMPLE_TICKER,
  SECURITY_ANALYSIS_PRIVATE_SAMPLE_TICKER,
];

export const FREE_PUBLIC_SECURITY_ANALYSIS_TICKERS = [
  SECURITY_ANALYSIS_PUBLIC_SAMPLE_TICKER,
];

export const FREE_SECURITY_ANALYSIS_DATASETS = {
  FINANCIALS: 'financials',
  JOBS: 'jobs',
  JOBS_DAILY: 'jobs-daily',
  SI: 'si',
  DEVMETRICS: 'devmetrics',
  DEVMETRICS_DAILY: 'devmetrics-daily',
  GUIDANCE: 'guidance',
  FILING_INTELLIGENCE: 'filing-intelligence',
};

export const FREE_SECURITY_ANALYSIS_COMPS_FILE_KEY = 'kpiCompsDataPlatforms.json';

export const isPublicResearchSampleTicker = (ticker) => (
  FREE_PUBLIC_SECURITY_ANALYSIS_TICKERS.includes(String(ticker || '').toUpperCase())
);

export const isFreeSecurityAnalysisTicker = (ticker) => (
  FREE_SECURITY_ANALYSIS_TICKERS.includes(String(ticker || '').toUpperCase())
);

export const getSecurityAnalysisSampleTicker = ({ ticker, isPrivate = false } = {}) => {
  const normalizedTicker = String(ticker || '').toUpperCase();
  if (isFreeSecurityAnalysisTicker(normalizedTicker)) return normalizedTicker;
  return isPrivate ? SECURITY_ANALYSIS_PRIVATE_SAMPLE_TICKER : SECURITY_ANALYSIS_PUBLIC_SAMPLE_TICKER;
};

export const getFreeSecurityAnalysisFileKey = (ticker, dataset) => (
  `securityanalysis/free/${String(ticker || '').toUpperCase()}/${dataset}.json`
);

export const getFreeSecurityAnalysisCdnUrl = (ticker, dataset) => (
  `${CDN_BASE_URL}/${getFreeSecurityAnalysisFileKey(ticker, dataset)}`
);

export const getPublicCompanyModelFileKey = (ticker) => (
  `securityanalysis/free/${String(ticker || '').toUpperCase()}/company-model.xlsx`
);

/**
 * Get financial data file key for a ticker
 * @param {string} ticker - Stock ticker symbol
 * @returns {string} File key path
 */
export const getFinancialFileKey = (ticker) => `securityanalysis/financial/${ticker}.json`;

/**
 * Get guidance vs actuals file key for a ticker
 * @param {string} ticker - Stock ticker symbol
 * @returns {string} File key path
 */
export const getGuidanceFileKey = (ticker) => `securityanalysis/guidance/${ticker}.json`;

/**
 * Get aggregate guidance analytics file key
 * @returns {string} File key path
 */
export const getGuidanceAggregateFileKey = () => 'securityanalysis/guidance/index.json';

export const SOURCE_CHECK_ID_PATTERN = /^sc_[a-f0-9]{64}$/;

/**
 * Get the immutable source-check record key shared by Core and Research storage.
 * @param {string} sourceCheckId - Content-addressed source-check identifier
 * @returns {string|null} Authenticated source-check file key
 */
export const getSourceCheckFileKey = (sourceCheckId) => {
  const normalized = String(sourceCheckId || '').trim().toLowerCase();
  if (!SOURCE_CHECK_ID_PATTERN.test(normalized)) return null;
  return `data/securityanalysis/source-checks/${normalized.slice(3, 5)}/${normalized}.json`;
};

/**
 * Get private company detail file key for a ticker
 * @param {string} ticker - Stock ticker symbol
 * @returns {string} File key path
 */
export const getPrivateFileKey = (ticker) => `securityanalysis/private/${ticker}.json`;

/**
 * Get acquisition snapshot compsheet file key for a ticker
 * @param {string} ticker - Stock ticker symbol
 * @returns {string} File key path
 */
export const getAcquisitionCompsheetFileKey = (ticker) => `securityanalysis/acquisition-compsheets/${ticker}.json`;

/**
 * Get news file key for a ticker
 * @param {string} ticker - Stock ticker symbol
 * @returns {string} File key path
 */
export const getNewsFileKey = (ticker) => `securityanalysis/news/${ticker}.json`;

/**
 * Get job postings file key for a ticker (per-ticker rich data with JobCountTrendWeekly)
 * @param {string} ticker - Stock ticker symbol
 * @returns {string} File key path
 */
export const getJobsFileKey = (ticker) => `securityanalysis/jobs/${ticker}.json`;

/** Research company jobs v3: compact dictionaries plus weekly membership. */
export const getResearchJobsV3FileKey = (ticker) => `securityanalysis/jobsV3/${ticker}.json`;

/** Research company jobs v2 fallback retained during the v3 rollout. */
export const getResearchJobsFileKey = (ticker) => `securityanalysis/jobsV2/${ticker}.json`;

/** Research company daily jobs v3: compact dictionaries plus daily membership. */
export const getResearchDailyJobsV3FileKey = (ticker) => `data/stockpage/jobsDailyV3/${ticker}.json`;

/** Research company daily jobs v2 fallback retained during the v3 rollout. */
export const getResearchDailyJobsFileKey = (ticker) => `data/stockpage/jobsDailyV2/${ticker}.json`;

/**
 * Get systems integrator partner jobs file key for an SI ticker.
 * @param {string} ticker - Systems integrator ticker or private ticker key
 * @returns {string} File key path
 */
export const getSIJobsFileKey = (ticker) => `securityanalysis/siJobs/${ticker}.json`;

/**
 * Get vendor-centric systems integrator partner jobs file key for a vendor ticker.
 * @param {string} ticker - Vendor ticker symbol
 * @returns {string} File key path
 */
export const getSIVendorJobsFileKey = (ticker) => `securityanalysis/siVendorJobs/${ticker}.json`;

/**
 * Get developer metrics project file key
 * @param {string} projectId - Project identifier (e.g., "AWS_DocumentDB")
 * @returns {string} File key path
 */
export const getDevmetricsProjectFileKey = (projectId) => `devmetrics/developerMetrics.${projectId}.json`;

/**
 * Get developer metrics company-scoped file key for a ticker
 * @param {string} ticker - Company ticker symbol
 * @returns {string} File key path
 */
export const getDevmetricsTickerFileKey = (ticker) => (
  `securityanalysis/devmetrics/${String(ticker || '').toUpperCase()}.json`
);

// ============================================
// API URL Builders
// ============================================

/**
 * Build URL for CDN data access (free/public data)
 * @param {string} fileName - File name to fetch
 * @returns {string} Full CDN URL
 */
export const getCdnUrl = (fileName) => `${CDN_BASE_URL}/${fileName}`;

/**
 * Build URL for rendered SEC source document access
 * @param {string} renderPath - Relative render path from sourceRef
 * @param {object} options
 * @param {string} options.quote - Optional quote to highlight in the rendered source
 * @returns {string|null} Full SEC source render URL
 */
export const getSecSourceRenderUrl = (renderPath, options = {}) => {
  if (!renderPath) return null;
  const cleanPath = String(renderPath).replace(/^\/+/, '');
  const url = `${SEC_SOURCE_CDN_BASE_URL}/${cleanPath}`;
  const quote = typeof options.quote === 'string' ? options.quote.trim() : '';
  if (!quote) return url;
  return `${url}?quote=${encodeURIComponent(quote)}`;
};

/**
 * Build URL for native SEC HTML source document access
 * @param {string} renderPath - Relative render path from sourceRef
 * @param {object} options
 * @param {string} options.quote - Optional quote to highlight via browser text fragments
 * @returns {string|null} Full native SEC source HTML URL
 */
export const getSecSourceNativeHtmlUrl = (renderPath, options = {}) => {
  if (!renderPath) return null;
  const cleanPath = String(renderPath).replace(/^\/+/, '');
  const htmlPath = cleanPath.replace(/\/render\.html$/, '/source.html');
  const url = `${SEC_SOURCE_CDN_BASE_URL}/${htmlPath}`;
  const quote = typeof options.quote === 'string' ? options.quote.trim() : '';
  if (!quote) return url;
  return `${url}#:~:text=${encodeURIComponent(quote)}`;
};

/**
 * Build URL for authenticated API data access
 * @param {string} fileKey - File key to fetch
 * @returns {string} Full API URL with query parameter
 */
export const getAuthenticatedUrl = (fileKey) => `${AUTH_VALIDATE_URL}?fileKey=${fileKey}`;

export const getResearchDataUrl = (fileKey) => `${RESEARCH_DATA_URL}?fileKey=${fileKey}`;

// ============================================
// Request Headers
// ============================================

/**
 * Get headers for authenticated API requests
 * @param {string} token - Auth0 access token
 * @returns {Object} Headers object for axios/fetch
 */
export const getAuthHeaders = (token) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${token}`,
});

/**
 * Get default headers for unauthenticated requests
 * @returns {Object} Headers object
 */
export const getDefaultHeaders = () => ({
  'Content-Type': 'application/json',
});
